<script setup lang="ts">
/**
 * Step 6 — Constitution + Documents overview. Constitution type (MODEL/CUSTM),
 * plus every uploaded file and generated document for the incorporation.
 */
import { computed } from 'vue';
import SgEfDocList from '../components/SgEfDocList.vue';
import SgEfDocUpload from '../components/SgEfDocUpload.vue';
import type { EfConstitution } from '../composables/incorporationEfilingModel';
import type { EfUploadedFile, EfGeneratedDoc } from '../composables/useIncorporationEfiling';

const props = defineProps<{
  constitution: EfConstitution;
  uploadedFiles: EfUploadedFile[];
  generatedDocs: EfGeneratedDoc[];
  readonly?: boolean;
}>();

const identityFiles = computed(() =>
  props.uploadedFiles.filter((f) => f.fileType === 'PASSPORT' || f.fileType === 'ADDR_PROF'),
);
const shareCapDocs = computed(() => props.uploadedFiles.filter((f) => f.fileType === 'ALLOT_DOC'));
const controllerDocs = computed(() => props.uploadedFiles.filter((f) => f.fileType === 'RC_DOC'));
</script>

<template>
  <div class="ef-step">
    <div class="ef-step__section">
      <div class="ef-step__h">Constitution</div>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-select
            v-model="constitution.type"
            :items="[
              { title: 'MODEL — standard constitution', value: 'MODEL' },
              { title: 'CUSTM — custom constitution', value: 'CUSTM' },
            ]"
            label="Constitution type"
            density="compact"
            variant="outlined"
            hide-details
            :readonly="readonly"
          />
        </v-col>
        <v-col v-if="constitution.type === 'MODEL'" cols="12" sm="6">
          <v-select
            v-model="constitution.modelType"
            :items="[
              { title: 'AT — as adopted', value: 'AT' },
              { title: 'TO — from time to time', value: 'TO' },
            ]"
            label="Model constitution type"
            density="compact"
            variant="outlined"
            hide-details
            :readonly="readonly"
          />
        </v-col>
      </v-row>

      <div v-if="constitution.type === 'CUSTM'" class="mt-2">
        <SgEfDocUpload
          :docs="constitution.documents"
          :max-count="5"
          :readonly="readonly"
          label="Custom constitution documents (PDF, up to 5)"
          hint="Uploaded to bizfile at submission."
        />
      </div>
    </div>

    <div class="ef-step__section">
      <div class="ef-step__h">Uploaded documents</div>

      <div class="ef-step__group">
        <SgEfDocList
          title="Passport / Proof of address"
          :uploaded="identityFiles"
          empty-text="No identity documents uploaded."
        />
      </div>
      <div class="ef-step__group">
        <SgEfDocList
          title="Share allotment documents"
          :uploaded="shareCapDocs"
          empty-text="No share allotment documents uploaded."
        />
      </div>
      <div class="ef-step__group">
        <SgEfDocList
          title="Controller supporting documents"
          :uploaded="controllerDocs"
          empty-text="No controller supporting documents uploaded."
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.ef-step__section {
  margin-bottom: 18px;
}
.ef-step__h {
  font-size: 14px;
  font-weight: 700;
  color: #1a2336;
  margin-bottom: 8px;
}
.ef-step__group {
  margin-bottom: 12px;
}
</style>
